// ==================== SELECT PLAN PAGE ====================

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Check, 
  Sparkles, 
  ArrowRight, 
  Crown,
  LogOut,
  Users,
  MessageSquare,
  FileText,
  Send,
  Building2,
  ChevronDown,
  ChevronUp,
  User,
  Settings,
  Info,
  Bot,
  Minus,
  Plus,
} from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Logo } from '../../components/ui/Logo';
import { useAuth } from '../../contexts';
import { CouponField } from '../../components/coupons/CouponField';
import type { PriceCalculation, CouponSummary } from '../../services/subscription.service';
import { usePlans } from '../../hooks';
import { subscriptionService } from '../../services';
import { cn } from '../../utils/helpers';
import { hasValidSubscriptionAccess } from '../../utils/subscription';
import { ROUTES } from '../../constants';
import { fadeInUp, staggerContainer, hoverScale, tapScale } from '../../utils/animations';
import type { SubscriptionPlan } from '../../types';
import type { SetupFeeDetails, LeadGenAddonDetails } from '../../services/subscription.service';
import { leadGenAddonCycleAmount } from '../../services/subscription.service';

// ==================== CONSTANTS ====================

const LIMIT_ICONS: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
  contacts: Users,
  messages: MessageSquare,
  templates: FileText,
  campaigns: Send,
};

// Format limit value - show "Unlimited" for -1
const formatLimitValue = (value: number): string => {
  if (value === -1) return 'Unlimited';
  return value.toLocaleString();
};

// ==================== PLAN CARD COMPONENT ====================

interface PlanCardProps {
  plan: SubscriptionPlan;
  isYearly: boolean;
  isSelected: boolean;
  onSelect: () => void;
}

const PlanCard = ({ plan, isYearly, isSelected, onSelect }: PlanCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const monthlyPrice = typeof plan.monthlyPrice === 'string' ? parseFloat(plan.monthlyPrice) : plan.monthlyPrice;
  const yearlyPrice = plan.yearlyPrice ? (typeof plan.yearlyPrice === 'string' ? parseFloat(plan.yearlyPrice) : plan.yearlyPrice) : monthlyPrice * 10;
  const setupFee = plan.setupFee ? (typeof plan.setupFee === 'string' ? parseFloat(plan.setupFee) : plan.setupFee) : 0;
  
  const displayPrice = isYearly ? yearlyPrice / 12 : monthlyPrice;
  const isPopular = plan.isPopular;
  const hasMoreFeatures = plan.features.length > 4;
  const displayedFeatures = isExpanded ? plan.features : plan.features.slice(0, 4);

  const limits = [
    { key: 'contacts', label: 'Contacts', value: formatLimitValue(plan.maxContacts) },
    { key: 'messages', label: 'Messages/mo', value: formatLimitValue(plan.maxMessagesPerMonth) },
    { key: 'templates', label: 'Templates', value: formatLimitValue(plan.maxTemplates) },
    { key: 'campaigns', label: 'Campaigns', value: formatLimitValue(plan.maxCampaigns) },
  ];

  return (
    <motion.div
      variants={fadeInUp}
      whileHover={hoverScale}
      whileTap={tapScale}
      onClick={onSelect}
      className={cn(
        'relative rounded-2xl p-6 cursor-pointer transition-all duration-300',
        'border-2',
        isSelected
          ? 'border-(--color-signal-orange) bg-(--color-orange-glow)'
          : 'border-(--color-border-gray) bg-(--color-charcoal-black)/50 hover:border-(--color-signal-orange)/50',
        isPopular && !isSelected && 'border-(--color-signal-orange)/30'
      )}
    >
      {/* Popular Badge */}
      {isPopular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <Badge variant="orange" className="whitespace-nowrap">
            <Crown size={12} className="mr-1" />
            Most Popular
          </Badge>
        </div>
      )}

      {/* Selection Indicator */}
      <div className={cn(
        'absolute top-4 right-4 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all',
        isSelected
          ? 'border-(--color-signal-orange) bg-(--color-signal-orange)'
          : 'border-(--color-border-gray)'
      )}>
        {isSelected && <Check size={14} className="text-white" />}
      </div>

      {/* Plan Name */}
      <h3 className="text-xl font-heading font-bold text-white mb-2">{plan.name}</h3>
      
      {/* Price */}
      <div className="mb-4">
        <div className="flex items-baseline gap-1">
          <span className="text-3xl font-bold text-(--color-signal-orange)">
            ${displayPrice.toFixed(2)}
          </span>
          <span className="text-(--color-muted-gray)">/month</span>
        </div>
        {isYearly && (
          <p className="text-xs text-(--color-success) mt-1">
            Billed ${yearlyPrice.toFixed(2)}/year (Save 17%)
          </p>
        )}
        {/* Setup Fee Badge */}
        {setupFee > 0 && (
          <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-(--color-signal-orange)/10 border border-(--color-signal-orange)/20">
            <Settings size={12} className="text-(--color-signal-orange)" />
            <span className="text-xs font-medium text-(--color-signal-orange)">
              +${setupFee.toFixed(0)} Setup Available
            </span>
          </div>
        )}
      </div>

      {/* Limits Grid */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        {limits.map((limit) => {
          const Icon = LIMIT_ICONS[limit.key] || Users;
          return (
            <div key={limit.key} className="flex items-center gap-2">
              <Icon size={14} className="text-(--color-signal-orange)" />
              <div>
                <p className="text-xs text-(--color-muted-gray)">{limit.label}</p>
                <p className="text-sm font-medium text-white">{limit.value}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Features */}
      <ul className="space-y-2">
        <AnimatePresence mode="sync">
          {displayedFeatures.map((feature, index) => (
            <motion.li 
              key={`${plan.id}-feature-${index}`}
              initial={index >= 4 ? { opacity: 0, height: 0 } : false}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="flex items-start gap-2 text-sm text-(--color-soft-gray)"
            >
              <Check size={14} className="text-(--color-success) shrink-0 mt-0.5" />
              <span>{feature}</span>
            </motion.li>
          ))}
        </AnimatePresence>
        {hasMoreFeatures && (
          <li>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(!isExpanded);
              }}
              className="flex items-center gap-1 text-xs text-(--color-signal-orange) hover:text-(--color-orange-light) 
                         font-medium transition-colors pl-5"
            >
              {isExpanded ? (
                <>
                  <ChevronUp size={14} />
                  Show less
                </>
              ) : (
                <>
                  <ChevronDown size={14} />
                  +{plan.features.length - 4} more features
                </>
              )}
            </button>
          </li>
        )}
      </ul>
    </motion.div>
  );
};

// ==================== SETUP FEE TOGGLE COMPONENT ====================

interface SetupFeeToggleProps {
  setupFeeDetails: SetupFeeDetails | null;
  includeSetupFee: boolean;
  onToggle: (include: boolean) => void;
}

const SetupFeeToggle = ({ setupFeeDetails, includeSetupFee, onToggle }: SetupFeeToggleProps) => {
  if (!setupFeeDetails?.enabled) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-(--color-navy-light) border border-(--color-border-gray) rounded-xl p-5"
    >
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-(--color-orange-glow) flex items-center justify-center shrink-0">
          <Settings size={24} className="text-(--color-signal-orange)" />
        </div>
        
        <div className="flex-1">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold text-white">
              WhatsApp Business Setup
            </h3>
            <span className="text-lg font-bold text-(--color-signal-orange)">
              ${setupFeeDetails.amount.toFixed(2)}
            </span>
          </div>
          
          <p className="text-sm text-(--color-soft-gray) mb-4">
            Our team will set up your WhatsApp Business API account and configure it for you.
          </p>
          
          <div className="flex items-center gap-4">
            <button
              onClick={() => onToggle(true)}
              className={cn(
                'flex-1 py-3 px-4 rounded-lg border-2 transition-all text-sm font-medium',
                includeSetupFee
                  ? 'border-(--color-signal-orange) bg-(--color-orange-glow) text-white'
                  : 'border-(--color-border-gray) text-(--color-muted-gray) hover:border-(--color-signal-orange)/50'
              )}
            >
              <Check size={16} className={cn('inline mr-2', !includeSetupFee && 'opacity-0')} />
              Yes, set up for me
            </button>
            
            <button
              onClick={() => onToggle(false)}
              className={cn(
                'flex-1 py-3 px-4 rounded-lg border-2 transition-all text-sm font-medium',
                !includeSetupFee
                  ? 'border-(--color-signal-orange) bg-(--color-orange-glow) text-white'
                  : 'border-(--color-border-gray) text-(--color-muted-gray) hover:border-(--color-signal-orange)/50'
              )}
            >
              <Check size={16} className={cn('inline mr-2', includeSetupFee && 'opacity-0')} />
              I'll set up myself
            </button>
          </div>
          
          <div className="flex items-start gap-2 mt-3 text-xs text-(--color-muted-gray)">
            <Info size={14} className="shrink-0 mt-0.5" />
            <span>
              If you choose to set up yourself, you'll need to configure your WhatsApp Business API credentials manually.
            </span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// ==================== LEAD GEN ADD-ON TOGGLE ====================

interface LeadGenAddonToggleProps {
  leadGenDetails: LeadGenAddonDetails | null;
  isYearly: boolean;
  includeLeadGenAddon: boolean;
  onToggle: (include: boolean) => void;
}

const LeadGenAddonToggle = ({ leadGenDetails, isYearly, includeLeadGenAddon, onToggle }: LeadGenAddonToggleProps) => {
  if (!leadGenDetails?.enabled) return null;

  const cycleAmount = leadGenAddonCycleAmount(leadGenDetails.amount, isYearly);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-(--color-navy-light) border border-(--color-border-gray) rounded-xl p-5"
    >
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-(--color-orange-glow) flex items-center justify-center shrink-0">
          <Bot size={24} className="text-(--color-signal-orange)" />
        </div>

        <div className="flex-1">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold text-white">Lead Generation Agent</h3>
            <span className="text-lg font-bold text-(--color-signal-orange)">
              ${cycleAmount.toFixed(2)}/{isYearly ? 'yr' : 'mo'}
            </span>
          </div>

          <p className="text-sm text-(--color-soft-gray) mb-4">
            Add Lead Ops: discover and enrich B2B leads, score ICP fit, and draft outreach — billed
            {isYearly ? ` yearly ($${leadGenDetails.amount.toFixed(2)}/mo × 12)` : ' monthly'} with your subscription.
          </p>

          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => onToggle(true)}
              className={cn(
                'flex-1 py-3 px-4 rounded-lg border-2 transition-all text-sm font-medium',
                includeLeadGenAddon
                  ? 'border-(--color-signal-orange) bg-(--color-orange-glow) text-white'
                  : 'border-(--color-border-gray) text-(--color-muted-gray) hover:border-(--color-signal-orange)/50'
              )}
            >
              <Check size={16} className={cn('inline mr-2', !includeLeadGenAddon && 'opacity-0')} />
              Yes, add Lead Ops
            </button>

            <button
              type="button"
              onClick={() => onToggle(false)}
              className={cn(
                'flex-1 py-3 px-4 rounded-lg border-2 transition-all text-sm font-medium',
                !includeLeadGenAddon
                  ? 'border-(--color-signal-orange) bg-(--color-orange-glow) text-white'
                  : 'border-(--color-border-gray) text-(--color-muted-gray) hover:border-(--color-signal-orange)/50'
              )}
            >
              <Check size={16} className={cn('inline mr-2', includeLeadGenAddon && 'opacity-0')} />
              No thanks
            </button>
          </div>

          <div className="flex items-start gap-2 mt-3 text-xs text-(--color-muted-gray)">
            <Info size={14} className="shrink-0 mt-0.5" />
            <span>
              If you skip this, you can still use AmplifyMsg; Lead Ops opens in the sidebar only when this add-on
              is on your account.
            </span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// ==================== ORGANIZATIONS SELECTOR ====================

/** Plan price for the chosen billing cycle (same as the order summary). */
const getPlanPrice = (plan: SubscriptionPlan, isYearly: boolean) => {
  const monthlyPrice = typeof plan.monthlyPrice === 'string'
    ? parseFloat(plan.monthlyPrice)
    : plan.monthlyPrice;
  const yearlyPrice = plan.yearlyPrice
    ? (typeof plan.yearlyPrice === 'string' ? parseFloat(plan.yearlyPrice) : plan.yearlyPrice)
    : monthlyPrice * 10;
  return isYearly ? yearlyPrice : monthlyPrice;
};

interface OrganizationCountSelectorProps {
  organizationCount: number;
  planPrice: number;
  isYearly: boolean;
  onChange: (count: number) => void;
}

const OrganizationCountSelector = ({ organizationCount, planPrice, isYearly, onChange }: OrganizationCountSelectorProps) => {
  const canDecrease = organizationCount > 1;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-(--color-navy-light) border border-(--color-border-gray) rounded-xl p-5"
    >
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-(--color-orange-glow) flex items-center justify-center shrink-0">
          <Building2 size={24} className="text-(--color-signal-orange)" />
        </div>

        <div className="flex-1">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold text-white">Accounts</h3>
            <span className="text-lg font-bold text-(--color-signal-orange)">
              ${planPrice.toFixed(2)}/{isYearly ? 'yr' : 'mo'} each
            </span>
          </div>

          <p className="text-sm text-(--color-soft-gray) mb-4">
            Each account connects its own WhatsApp Business number. The plan price, and the setup fee if
            you choose it, is charged for each account.
          </p>

          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => onChange(organizationCount - 1)}
              disabled={!canDecrease}
              aria-label="Fewer accounts"
              className={cn(
                'py-3 px-4 rounded-lg border-2 transition-all text-sm font-medium',
                canDecrease
                  ? 'border-(--color-border-gray) text-(--color-muted-gray) hover:border-(--color-signal-orange)/50'
                  : 'border-(--color-border-gray) text-(--color-muted-gray) opacity-50 cursor-not-allowed'
              )}
            >
              <Minus size={16} />
            </button>

            <div className="flex-1 py-3 px-4 rounded-lg border-2 border-(--color-signal-orange) bg-(--color-orange-glow) text-white text-sm font-medium text-center">
              {organizationCount} {organizationCount === 1 ? 'account' : 'accounts'}
            </div>

            <button
              type="button"
              onClick={() => onChange(organizationCount + 1)}
              aria-label="More accounts"
              className="py-3 px-4 rounded-lg border-2 transition-all text-sm font-medium border-(--color-border-gray) text-(--color-muted-gray) hover:border-(--color-signal-orange)/50"
            >
              <Plus size={16} />
            </button>
          </div>

          <div className="flex items-start gap-2 mt-3 text-xs text-(--color-muted-gray)">
            <Info size={14} className="shrink-0 mt-0.5" />
            <span>
              You can add more accounts later from Settings → WhatsApp Integration.
            </span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// ==================== PRICE SUMMARY COMPONENT ====================

interface PriceSummaryProps {
  selectedPlan: SubscriptionPlan | null;
  isYearly: boolean;
  includeSetupFee: boolean;
  setupFeeAmount: number;
  includeLeadGenAddon: boolean;
  leadGenAddonAmount: number;
  organizationCount: number;
  quote?: PriceCalculation | null;
}

const PriceSummary = ({
  selectedPlan,
  isYearly,
  includeSetupFee,
  setupFeeAmount,
  includeLeadGenAddon,
  leadGenAddonAmount,
  organizationCount,
  quote,
}: PriceSummaryProps) => {
  if (!selectedPlan) return null;

  const monthlyPrice = typeof selectedPlan.monthlyPrice === 'string' 
    ? parseFloat(selectedPlan.monthlyPrice) 
    : selectedPlan.monthlyPrice;
  const yearlyPrice = selectedPlan.yearlyPrice 
    ? (typeof selectedPlan.yearlyPrice === 'string' ? parseFloat(selectedPlan.yearlyPrice) : selectedPlan.yearlyPrice) 
    : monthlyPrice * 10;
  
  // The plan price and the setup fee are charged per organization
  const planAmount = quote?.planAmount ?? (isYearly ? yearlyPrice : monthlyPrice) * organizationCount;
  const setupFee = quote?.setupFee ?? (includeSetupFee ? setupFeeAmount * organizationCount : 0);
  const perOrganization = organizationCount > 1 ? ` × ${organizationCount} accounts` : '';
  // leadGenAddonAmount is the monthly fee; it's billed for the whole cycle.
  const leadGen = quote?.leadGenAddon ?? (includeLeadGenAddon ? leadGenAddonCycleAmount(leadGenAddonAmount, isYearly) : 0);
  const totalAmount = quote?.amountDueNow ?? (planAmount + setupFee + leadGen);
  const recurringAmount = quote?.nextRenewalAmount ?? (planAmount + leadGen);

  return (
    <div className="bg-(--color-navy-light) border border-(--color-border-gray) rounded-xl p-5">
      <h3 className="text-lg font-semibold text-white mb-4">Order Summary</h3>
      
      <div className="space-y-3">
        <div className="flex justify-between text-sm">
          <span className="text-(--color-soft-gray)">
            {selectedPlan.name} Plan ({isYearly ? 'Yearly' : 'Monthly'}){perOrganization}
          </span>
          <span className="text-white font-medium">${planAmount.toFixed(2)}</span>
        </div>
        
        {includeSetupFee && (
          <div className="flex justify-between text-sm">
            <span className="text-(--color-soft-gray)">WhatsApp Setup Fee (one-time){perOrganization}</span>
            <span className="text-white font-medium">${setupFee.toFixed(2)}</span>
          </div>
        )}

        {includeLeadGenAddon && leadGen > 0 && (
          <div className="flex justify-between text-sm">
            <span className="text-(--color-soft-gray)">
              Lead Generation Agent ({isYearly ? '12 months' : 'monthly'})
            </span>
            <span className="text-white font-medium">${leadGen.toFixed(2)}</span>
          </div>
        )}
        
        {!!quote?.discountAmount && (
          <div className="flex justify-between text-sm text-(--color-success)">
            <span>Discount ({quote.coupon?.code})</span>
            <span>−${Number(quote.discountAmount).toFixed(2)}</span>
          </div>
        )}
        <div className="border-t border-(--color-border-gray) pt-3 mt-3">
          <div className="flex justify-between">
            <span className="text-white font-semibold">Total Due Today</span>
            <span className="text-xl font-bold text-(--color-signal-orange)">${totalAmount.toFixed(2)}</span>
          </div>
          <p className="text-xs text-(--color-muted-gray) mt-1">
            Then ${recurringAmount.toFixed(2)}/{isYearly ? 'year' : 'month'} after
          </p>
        </div>
      </div>
    </div>
  );
};

// ==================== MAIN COMPONENT ====================

const SelectPlanPage = () => {
  const navigate = useNavigate();
  const [isYearly, setIsYearly] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [includeSetupFee, setIncludeSetupFee] = useState(false);
  const [setupFeeDetails, setSetupFeeDetails] = useState<SetupFeeDetails | null>(null);
  const [includeLeadGenAddon, setIncludeLeadGenAddon] = useState(false);
  const [leadGenDetails, setLeadGenDetails] = useState<LeadGenAddonDetails | null>(null);
  const [organizationCount, setOrganizationCount] = useState(1);
  const [couponInput, setCouponInput] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [currentCoupon, setCurrentCoupon] = useState<CouponSummary | null>(null);
  const [couponLookupComplete, setCouponLookupComplete] = useState(false);
  const [couponLookupFailed, setCouponLookupFailed] = useState(false);
  const [couponQuote, setCouponQuote] = useState<{ key: string; data: PriceCalculation } | null>(null);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user, logout } = useAuth();
  const { plans, isLoading: plansLoading, error: plansError } = usePlans();

  // If a plan becomes active while this page is open (e.g. an admin assigns one
  // for an offline payment), take the customer to their dashboard. Only on the
  // change, so a customer who opens this page deliberately isn't bounced away.
  const hasPlanAccess = hasValidSubscriptionAccess(user?.organization?.subscription);
  const hadPlanAccess = useRef(hasPlanAccess);
  useEffect(() => {
    if (!hadPlanAccess.current && hasPlanAccess) {
      navigate(ROUTES.CUSTOMER.DASHBOARD, { replace: true });
    }
    hadPlanAccess.current = hasPlanAccess;
  }, [hasPlanAccess, navigate]);

  const selectedPlan = plans.find(p => p.id === selectedPlanId) || null;
  const hasCheckoutAddons =
    Boolean(setupFeeDetails?.enabled) || Boolean(leadGenDetails?.enabled);

  // Fetch setup fee & lead-gen add-on details when a plan is selected (same pattern as WhatsApp setup)
  useEffect(() => {
    const fetchAddonDetails = async () => {
      if (!selectedPlanId) {
        setSetupFeeDetails(null);
        setLeadGenDetails(null);
        return;
      }

      try {
        const [setupRes, leadRes] = await Promise.all([
          subscriptionService.getSetupFeeDetails(selectedPlanId),
          subscriptionService.getLeadGenAddonDetails(selectedPlanId),
        ]);

        if (setupRes.success && setupRes.data) {
          setSetupFeeDetails(setupRes.data);
          if (!setupRes.data.enabled) {
            setIncludeSetupFee(false);
          }
        } else {
          setSetupFeeDetails(null);
        }

        if (leadRes.success && leadRes.data) {
          setLeadGenDetails(leadRes.data);
          if (!leadRes.data.enabled) {
            setIncludeLeadGenAddon(false);
          }
        } else {
          setLeadGenDetails(null);
        }
      } catch (err) {
        console.error('Failed to fetch add-on details:', err);
        setSetupFeeDetails(null);
        setLeadGenDetails(null);
      }
    };
    fetchAddonDetails();
  }, [selectedPlanId]);

  const quoteKey = JSON.stringify([selectedPlanId, isYearly, includeSetupFee, includeLeadGenAddon, organizationCount, couponCode]);
  // A returning customer's current coupon applies without typing it. If it can't be
  // looked up, the summary always comes from the server quote, which applies it anyway.
  const needsQuote = !!couponCode || !!currentCoupon || couponLookupFailed;
  const matchingQuote = couponQuote?.key === quoteKey ? couponQuote.data : null;
  useEffect(() => {
    let cancelled = false;
    void subscriptionService.getCurrentSubscription().then(response => {
      if (cancelled) return;
      if (response.success) setCurrentCoupon(response.data?.coupon ?? null);
      else setCouponLookupFailed(true);
      setCouponLookupComplete(true);
    });
    return () => { cancelled = true; };
  }, []);
  useEffect(() => {
    if (!selectedPlanId || !needsQuote) return;
    let cancelled = false;
    void subscriptionService.calculatePrice({ planId: selectedPlanId, billingCycle: isYearly ? 'YEARLY' : 'MONTHLY',
      includeSetupFee, includeLeadGenAddon, organizationCount, ...(couponCode && { couponCode }) }).then(response => {
      if (cancelled) return;
      if (response.success && response.data) {
        setCouponQuote({ key: quoteKey, data: response.data });
        setCouponError(null);
      } else { setCouponQuote(null); setCouponError(response.message); }
    });
    return () => { cancelled = true; };
  }, [selectedPlanId, isYearly, includeSetupFee, includeLeadGenAddon, organizationCount, couponCode, needsQuote, quoteKey]);

  const handleSelectPlan = async () => {
    if (!selectedPlanId || !couponLookupComplete || (needsQuote && !matchingQuote)) return;
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      // Create checkout session
      const response = await subscriptionService.createCheckout({
        planId: selectedPlanId,
        billingCycle: isYearly ? 'YEARLY' : 'MONTHLY',
        includeSetupFee,
        includeLeadGenAddon,
        organizationCount,
        ...(couponCode && { couponCode }),
      });

      if (response.success && response.data) {
        // Navigate to payment page with checkout data
        navigate('/app/payment/checkout', {
          state: {
            checkoutSessionId: response.data.checkoutSessionId,
            clientSecret: response.data.clientSecret,
            subscriptionId: response.data.subscriptionId,
            priceDetails: response.data.priceDetails,
          },
        });
      } else {
        setError(response.message || 'Failed to create checkout session');
      }
    } catch (err) {
      console.error('Error creating checkout:', err);
      setError(err instanceof Error ? err.message : 'An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-(--color-dark-navy)">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-(--color-midnight-navy)/95 backdrop-blur-xl">
        <div className="container-custom flex items-center justify-between h-18 py-3">
          <Logo size="lg" />
          
          <div className="flex items-center gap-6">
            {user && (
              <div className="hidden sm:flex items-center gap-3">
                {/* User Avatar */}
                <div className="w-10 h-10 rounded-full bg-linear-to-br from-(--color-signal-orange) to-(--color-orange-light) 
                                flex items-center justify-center shadow-lg shadow-(--color-signal-orange)/20">
                  <User size={18} className="text-white" />
                </div>
                
                {/* User Info */}
                <div className="flex flex-col">
                  <span className="text-sm font-semibold text-white">
                    {user.firstName && user.lastName 
                      ? `${user.firstName} ${user.lastName}` 
                      : user.firstName || 'Customer'}
                  </span>
                  {user.organization?.name && (
                    <span className="text-xs text-(--color-muted-gray) flex items-center gap-1">
                      <Building2 size={10} />
                      {user.organization.name}
                    </span>
                  )}
                </div>
              </div>
            )}
            
            {/* Sign Out Button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={logout}
              leftIcon={<LogOut size={16} />}
              className="text-(--color-muted-gray) hover:text-white hover:bg-(--color-border-gray)/30"
            >
              <span className="hidden md:inline">Sign Out</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container-custom py-12">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
        >
          {/* Page Title */}
          <motion.div variants={fadeInUp} className="text-center mb-10">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-(--color-orange-glow) mb-4">
              <Sparkles size={32} className="text-(--color-signal-orange)" />
            </div>
            <h1 className="text-3xl lg:text-4xl font-heading font-bold text-white mb-3">
              Choose Your Plan
            </h1>
            <p className="text-(--color-soft-gray) max-w-xl mx-auto">
              Welcome, {user?.firstName}! Select a subscription plan to unlock the full power of 
              AmplifyMsg and start growing your business with WhatsApp automation.
            </p>
          </motion.div>

          {/* Billing Toggle */}
          <motion.div variants={fadeInUp} className="flex justify-center mb-8">
            <div className="inline-flex items-center gap-3 p-1 rounded-xl bg-(--color-navy-light) border border-(--color-border-gray)">
              <button
                onClick={() => { setError(null); setIsYearly(false); }}
                className={cn(
                  'px-4 py-2 rounded-lg text-sm font-medium transition-all',
                  !isYearly
                    ? 'bg-(--color-signal-orange) text-white'
                    : 'text-(--color-muted-gray) hover:text-white'
                )}
              >
                Monthly
              </button>
              <button
                onClick={() => { setError(null); setIsYearly(true); }}
                className={cn(
                  'px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2',
                  isYearly
                    ? 'bg-(--color-signal-orange) text-white'
                    : 'text-(--color-muted-gray) hover:text-white'
                )}
              >
                Yearly
                <Badge variant="success" size="sm">Save 17%</Badge>
              </button>
            </div>
          </motion.div>

          {/* Plans Grid */}
          {plansLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin w-8 h-8 border-2 border-(--color-signal-orange) border-t-transparent rounded-full" />
            </div>
          ) : plansError ? (
            <div className="text-center py-12">
              <p className="text-(--color-error)">{plansError}</p>
            </div>
          ) : plans.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-(--color-muted-gray)">No plans available at the moment.</p>
            </div>
          ) : (
            <motion.div
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
              className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10"
            >
              {plans.map((plan) => (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  isYearly={isYearly}
                  isSelected={selectedPlanId === plan.id}
                  onSelect={() => { setError(null); setSelectedPlanId(plan.id); }}
                />
              ))}
            </motion.div>
          )}

          {/* Setup Fee & Summary Section */}
          <AnimatePresence>
            {selectedPlan && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                className="grid gap-6 mb-10 lg:grid-cols-2"
              >
                <div className="space-y-6">
                  <OrganizationCountSelector
                    organizationCount={organizationCount}
                    planPrice={getPlanPrice(selectedPlan, isYearly)}
                    isYearly={isYearly}
                    onChange={(count) => { setError(null); setOrganizationCount(count); }}
                  />
                  {hasCheckoutAddons ? (
                    <>
                      <SetupFeeToggle
                        setupFeeDetails={setupFeeDetails}
                        includeSetupFee={includeSetupFee}
                        onToggle={(include) => { setError(null); setIncludeSetupFee(include); }}
                      />
                      <LeadGenAddonToggle
                        leadGenDetails={leadGenDetails}
                        isYearly={isYearly}
                        includeLeadGenAddon={includeLeadGenAddon}
                        onToggle={(include) => { setError(null); setIncludeLeadGenAddon(include); }}
                      />
                    </>
                  ) : null}
                </div>
                <div className="space-y-4">
                <PriceSummary
                  selectedPlan={selectedPlan}
                  isYearly={isYearly}
                  includeSetupFee={includeSetupFee}
                  setupFeeAmount={setupFeeDetails?.amount || 0}
                  includeLeadGenAddon={includeLeadGenAddon}
                  leadGenAddonAmount={leadGenDetails?.amount || 0}
                  organizationCount={organizationCount}
                  quote={matchingQuote}
                />
                <CouponField
                  value={couponInput}
                  onChange={setCouponInput}
                  onApply={(code) => {
                    setError(null);
                    setCouponError(null);
                    setCouponCode(code);
                  }}
                  appliedCode={couponCode}
                  coupon={matchingQuote?.coupon ?? currentCoupon}
                  error={couponError}
                  isLoading={needsQuote && !matchingQuote && !couponError}
                  disabled={isSubmitting}
                />
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Error Message */}
          {error && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mb-6 p-4 rounded-lg bg-(--color-error)/10 border border-(--color-error)/20"
            >
              <p className="text-(--color-error) text-sm">{error}</p>
            </motion.div>
          )}

          {/* Selected Plan Action */}
          <AnimatePresence>
            {selectedPlanId && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                className="fixed bottom-0 left-0 right-0 p-4 bg-(--color-midnight-navy)/95 backdrop-blur-md"
              >
                <div className="container-custom flex items-center justify-between">
                  <div>
                    <p className="text-sm text-(--color-muted-gray)">Selected Plan</p>
                    <p className="text-lg font-semibold text-white">
                      {plans.find(p => p.id === selectedPlanId)?.name}
                    </p>
                  </div>
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={handleSelectPlan}
                    isLoading={isSubmitting}
                    disabled={!couponLookupComplete || (needsQuote && !matchingQuote)}
                    rightIcon={<ArrowRight size={18} />}
                  >
                    Continue to Payment
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Features Comparison Note */}
          <motion.div
            variants={fadeInUp}
            className="text-center mt-8 mb-20"
          >
            <p className="text-sm text-(--color-muted-gray)">
              All plans include WhatsApp Business API integration, analytics dashboard, and email support.
            </p>
            <p className="text-sm text-(--color-muted-gray) mt-1">
              Need a custom enterprise solution?{' '}
              <a href="/contact" className="text-(--color-signal-orange) hover:underline">
                Contact our sales team
              </a>
            </p>
          </motion.div>
        </motion.div>
      </main>
    </div>
  );
};

export { SelectPlanPage };
// Comment Pushed
